import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Work, WorkFilters } from "../../types/work";
import EntityTable from "../EntityTable";
import { renderRowViewAndUpdateActions } from "../../common/TableUtils";
import {
  extractWorkEditLink,
  extractWorkViewLink,
} from "../entitiesReferences/InternalEntityLink";
import { MRT_ColumnDef } from "material-react-table";
import useWorkQuery from "../../hooks/query/useWorkQuery";
import useWorkLogic from "../../hooks/logic/workLogic";
import useServerFilters from "../../hooks/useServerFilters";
import { workFilterSpec } from "../../common/entities/WorkColumns";

interface WorkTableProps {
  columns?: MRT_ColumnDef<Work, any>[];
  hiddenColumnIds?: Extract<keyof Work, string>[];
  filters: WorkFilters;
  enableRowActions?: boolean;
}

const WorkTable: React.FC<WorkTableProps> = ({
  columns,
  hiddenColumnIds,
  filters,
  enableRowActions = true,
}) => {
  const {
    serverFilters,
    entityTableFilterProps,
    pagination,
    setPagination,
  } = useServerFilters(filters, workFilterSpec);
  const { useFetchAll } = useWorkQuery(filters.system_language_type);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: serverFilters,
    externalPagination: pagination,
    externalSetPagination: setPagination,
  });
  const { languageBasedColumns } = useWorkLogic(filters.system_language_type);
  const navigate = useNavigate();

  const renderRowActions = useMemo(
    () =>
      renderRowViewAndUpdateActions(
        (work: Work) => navigate(extractWorkViewLink(work)),
        (work: Work) => navigate(extractWorkEditLink(work))
      ),
    [navigate]
  );

  return (
    <EntityTable
      loading={isLoading}
      error={errorMessage}
      columns={columns || languageBasedColumns}
      hiddenColumnIds={hiddenColumnIds}
      renderRowActions={renderRowActions}
      enableRowActions={enableRowActions}
      data={data?.data || []}
      pagination={pagination}
      manualPagination={true}
      onPaginationChange={setPagination}
      totalRowCount={data?.total || 0}
      manualFiltering
      {...entityTableFilterProps}
    />
  );
};

export default WorkTable;
